// The QUOTE slice's data layer (Module 09 §5's quote / review / assignment /
// order rows, slice P3-C-1) — TanStack Query over the generated api-client,
// same posture as hooks/sales-queries.ts: doctrine 3 means apps/web talks to
// the API only through `@assessly/api-client`, every mutation carries a fresh
// Idempotency-Key, every queryFn threads `{signal}`, and every mutation
// invalidates exactly the reads it can have moved.
//
// IT IS A SECOND FILE, not an extension of sales-queries.ts, for one reason
// that is about people rather than code: P3-A's foundations file is stable and
// read by five landed screens, and this slice was built alongside two other
// agents writing in the same tree. A new surface in its own file cannot
// collide with an edit to theirs. The two are imported side by side by the
// screens that need both.
//
// WHAT IS ON THE WIRE AND WHAT IS NOT — the review inbox is where this bites,
// so it is stated here rather than discovered in a screen: §4.6 describes FIVE
// card kinds and FOUR of them decide. Quote margin breaches decide through
// `POST /v1/quotes/{id}/decide`; credit blocks release through
// `POST /v1/orders/{id}/credit-decision`; the override decide
// (`/v1/customer-price-overrides/{id}/decide`) landed with P3-C-2 (D-263);
// and the direct-order decide (`/v1/orders/{id}/decide`) landed with P3-D
// PR-D1 (D-283) beside the `POST /v1/orders` that produces its card — both
// live in hooks/order-mutations.ts (`useCreateOrder`, `useDecideOrder`), a
// third file so the two screens built against them in parallel could not
// overwrite each other. The fifth kind, `fulfillment_degradation`, is a notice
// and has no verb. A decide button whose endpoint 404s is the exact defect
// D-088 is about, and it is prevented HERE, in the layer that knows which
// routes exist, rather than in three screens that would each have to remember.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap, unwrapVoid } from "../../../../lib/request";
import { salesKeys } from "./sales-queries";
import { keepPageOnTurn } from "../../../../lib/cursor-pages";

type S = components["schemas"];

export type Quote = S["QuoteListItem"];
export type QuoteDetail = S["QuoteDetailResponse"];
export type QuoteRevision = S["QuoteRevision"];
export type QuoteLine = S["QuoteLine"];
export type QuoteLineInput = S["QuoteLineInput"];
export type QuoteStatus = S["QuoteStatus"];
export type QuoteOrigin = S["QuoteOrigin"];
export type QuoteCreateRequest = S["QuoteCreateRequest"];
export type QuoteRevisionCreateRequest = S["QuoteRevisionCreateRequest"];
export type QuoteSendRequest = S["QuoteSendRequest"];
export type QuoteSendResponse = S["QuoteSendResponse"];
export type QuoteDecideRequest = S["QuoteDecideRequest"];
export type QuoteCancelRequest = S["QuoteCancelRequest"];
export type QuoteRecordRejectionRequest = S["QuoteRecordRejectionRequest"];
/** §2.12's lost-reason vocabulary (`price` / `lead_time` / `stock` / `other`)
 * — a CODE, never free prose, because P3-E's loss analysis counts it. */
export type QuoteRejectionReason = S["QuoteRejectionReason"];
export type QuoteDuplicateRequest = S["QuoteDuplicateRequest"];
export type QuoteDuplicateResponse = S["QuoteDuplicateResponse"];
export type QuoteRemindResponse = S["QuoteRemindResponse"];
/** The composer's per-line fulfilment picture. The contract calls the zod
 * object `LineFulfillmentState` but registers it under the RESPONSE name — one
 * schema, one OpenAPI component — so the generated client knows it as
 * `FulfillmentOptionsResponse` and that is the name to read it by. */
export type LineFulfillmentState = S["FulfillmentOptionsResponse"];
export type CustomerNotification = S["CustomerNotification"];
export type CustomerNotificationKind = S["CustomerNotificationKind"];
export type CustomerNotificationStatus = S["CustomerNotificationStatus"];
export type CustomerNotificationListResponse = S["CustomerNotificationListResponse"];
export type FulfillmentOptionsRequest = S["FulfillmentOptionsRequest"];
export type ReviewCard = S["ReviewCard"];
export type ReviewCardKind = S["ReviewCardKind"];
export type CreditDecisionRequest = S["CreditDecisionRequest"];
// §2.9 override writes (slice P3-C-2, D-263) — read side in sales-queries.ts.
export type CustomerPriceOverrideCreateRequest = S["CustomerPriceOverrideCreateRequest"];
export type CustomerPriceOverrideDecideRequest = S["CustomerPriceOverrideDecideRequest"];
export type CustomerPriceOverrideRevokeRequest = S["CustomerPriceOverrideRevokeRequest"];
export type SalesOrder = S["OrderListItem"];
export type OrderDetail = S["OrderDetailResponse"];
export type OrderLine = S["OrderLine"];
export type OrderStatus = S["OrderStatus"];
export type CreditCheckStatus = S["CreditCheckStatus"];
export type CustomerAssignmentCreateRequest = S["CustomerAssignmentCreateRequest"];

export type QuotesQuery = NonNullable<paths["/v1/quotes"]["get"]["parameters"]["query"]>;
export type OrdersQuery = NonNullable<paths["/v1/orders"]["get"]["parameters"]["query"]>;
export type ReviewsQuery = NonNullable<paths["/v1/sales/reviews"]["get"]["parameters"]["query"]>;
export type CustomerNotificationsQuery = NonNullable<
  paths["/v1/customer-notifications"]["get"]["parameters"]["query"]
>;

export const quoteKeys = {
  quotes: (query: QuotesQuery, cursor: string | undefined) =>
    ["sales", "quotes", query, cursor ?? null] as const,
  quoteLists: () => ["sales", "quotes"] as const,
  quote: (id: string) => ["sales", "quote", id] as const,
  revisions: (id: string) => ["sales", "quote-revisions", id] as const,
  reviews: (query: ReviewsQuery, cursor: string | undefined) =>
    ["sales", "reviews", query, cursor ?? null] as const,
  reviewLists: () => ["sales", "reviews"] as const,
  orders: (query: OrdersQuery, cursor: string | undefined) =>
    ["sales", "orders", query, cursor ?? null] as const,
  orderLists: () => ["sales", "orders"] as const,
  order: (id: string) => ["sales", "order", id] as const,
  // The §2.20 log read (`GET /v1/customer-notifications`, P3-E PR-E1). One
  // prefix for every filter, so a send or a remind invalidates whichever
  // document's card is mounted without knowing its filter.
  notifications: (query: CustomerNotificationsQuery) =>
    ["sales", "customer-notifications", query] as const,
  notificationLists: () => ["sales", "customer-notifications"] as const,
};

/** Every deal read's prefix, `opportunityKeys.all()` in opportunity-queries.ts.
 * It is restated here because that file imports this one, and
 * __tests__/document-deal-reads.test.tsx pins the two equal. */
export const DEAL_READS_PREFIX = ["sales", "opportunities"] as const;

/**
 * THE DEAL READS A DOCUMENT WRITE CAN MOVE (P3-E2 PR E2-1, spec 09 §3.11).
 * An order a deal was won by and that is then cancelled leaves the deal won,
 * and the deal page and the forecast's cancelled count read that (§9 item
 * 31). A line cancel on the last live line cancels the whole order. A decide
 * moves an order out of pending approval, and a deal reads its won order's
 * status. A quote created on a deal joins the deal's linked quotes. The
 * inactive forecast and board are dropped for the reason opportunity-queries.ts
 * gives for every deal write, so a remount starts from the skeleton and never
 * paints figures that then change under the reader.
 */
export function invalidateDealReadsFromDocument(qc: QueryClient): void {
  qc.removeQueries({ queryKey: [...DEAL_READS_PREFIX, "forecast"], type: "inactive" });
  qc.removeQueries({ queryKey: [...DEAL_READS_PREFIX, "board"], type: "inactive" });
  void qc.invalidateQueries({ queryKey: DEAL_READS_PREFIX });
}

// ---------------------------------------------------------------------------
// §4.5 quote list / §4.4 composer reads
// ---------------------------------------------------------------------------

/**
 * ONE PAGE of the §4.5 quote list — the request `useQuotes` makes, as a plain
 * function so the CSV export walks the SAME read with the screen's own filters
 * and sort (D-248, D-289 ruling 1). Two copies of a list request drift; the
 * day one of them gains a parameter the export would quietly describe a
 * different list from the one on screen.
 */
export async function fetchQuotesPage(
  query: QuotesQuery,
  cursor: string | undefined,
  signal?: AbortSignal,
) {
  const res = await client.GET("/v1/quotes", {
    ...(signal !== undefined ? { signal } : {}),
    params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
  });
  return unwrap(res, "Could not load the quotes");
}

export function useQuotes(query: QuotesQuery, cursor: string | undefined, enabled = true) {
  return useQuery({
    queryKey: quoteKeys.quotes(query, cursor),
    enabled,
    // A page turn keeps the page shown until the next lands (D-560).
    placeholderData: keepPageOnTurn(quoteKeys.quotes(query, cursor)),
    queryFn: async ({ signal }) => fetchQuotesPage(query, cursor, signal),
  });
}

/**
 * EVERY PAGE A PICKER'S WALK HOLDS, one query per cursor under the key
 * `useQuotes` reads (spec 09 §4.14's Link a quote, #293). Each page stays an
 * observed query for as long as the dialog is open, so an invalidation of
 * `quoteKeys.quoteLists()` reads every walked page again and a row on page one
 * is judged on its fresh facts, never on the snapshot Show more once took.
 * Results come back in the order of `cursors`.
 */
export function useQuotePages(query: QuotesQuery, cursors: readonly (string | undefined)[]) {
  return useQueries({
    queries: cursors.map((cursor) => ({
      queryKey: quoteKeys.quotes(query, cursor),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchQuotesPage(query, cursor, signal),
    })),
  });
}

export function useQuote(id: string) {
  return useQuery({
    queryKey: quoteKeys.quote(id),
    enabled: id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quotes/{id}", { signal, params: { path: { id } } });
      return unwrap(res, "Could not load the quote");
    },
  });
}

/** §5 revision history — LAZY (the composer's version panel opens it), never
 * part of the composer's first paint. */
export function useQuoteRevisions(id: string, enabled: boolean) {
  return useQuery({
    queryKey: quoteKeys.revisions(id),
    enabled: enabled && id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quotes/{id}/revisions", {
        signal,
        params: { path: { id } },
      });
      return unwrap(res, "Could not load the versions").items;
    },
  });
}

// ---------------------------------------------------------------------------
// §2.20 customer notifications — what the customer was told (P3-E PR-E1,
// D-291). Read by §4.7's panel off `OrderDetailResponse.notifications`, and by
// the composer's Notifications card off `GET /v1/customer-notifications`.
// ---------------------------------------------------------------------------

/** How often a surface re-reads while a row is `queued` and due: the
 * dispatcher polls every `SALES_NOTIFY_POLL_MS` (5 s by default), so a queued
 * row normally settles within one or two of its passes. */
export const NOTIFICATION_FAST_POLL_MS = 3_000;
/** …and while only rows that move on a slower clock are pending: a `held`
 * row (released when the tenant's CURRENT quiet hours stop covering now — a
 * shortened window releases it early, so there is no instant to wait for), a
 * `queued` row waiting out a retry backoff, or a `queued` row the dispatcher
 * has left alone longer than the fast window. */
export const NOTIFICATION_SLOW_POLL_MS = 15_000;
/** How long a due `queued` row keeps the FAST cadence, from its `created_at`.
 * Past it, something is holding the dispatcher up (a deploy roll, a stopped
 * worker), and three-second reads would not bring the row any sooner. */
export const NOTIFICATION_FAST_WINDOW_MS = 120_000;

/**
 * THE BOUNDED POLL (D-291; the D-279 rule, applied to §2.20). A surface
 * showing customer notifications re-reads ONLY while a row can still move on
 * its own — `queued` (the dispatcher will send, hold or skip it) or `held`
 * (quiet hours will release it) — and stops the moment none can: every other
 * status only moves through a person's press, which invalidates the read
 * itself, or through module 17's delivery rollup, which is not worth a timer.
 *
 * WITHOUT IT the row would read `Sending…` for ever. The transition writes
 * the row `queued` in its own transaction and the dispatcher sends it seconds
 * later, so the order detail fetched straight after a confirm, a release or a
 * cancel always catches it `queued` — and a status nobody refreshes is the
 * D-279 flake: a screen reporting a message as still going out while the
 * worker's own log says it went.
 *
 * WHY TWO CADENCES. A due `queued` row settles in seconds, so the read comes
 * back in seconds. Nothing else pending has an instant the client could wait
 * for: a held row's `send_after` is only the display value (a settings change
 * releases it earlier), and a backoff's `send_after` is when the dispatcher
 * next TRIES, not when it succeeds. Those re-read slowly until they settle.
 * The fast window is measured on the browser's clock against the server's
 * `created_at`, so a skewed clock only moves a row between the two cadences —
 * it never stops a poll that has something to wait for.
 *
 * TanStack runs no interval in a background tab, so a page left open behind
 * others costs nothing.
 *
 * Exported as a pure function so the rule is pinned on the rows it reads,
 * not on a timer in a rendered screen.
 */
export function notificationRefetchInterval(
  rows: readonly Pick<CustomerNotification, "status" | "send_after" | "created_at">[] | undefined,
  nowMs: number,
): number | false {
  if (rows === undefined) return false;
  let pending = false;
  for (const row of rows) {
    if (row.status === "held") {
      pending = true;
      continue;
    }
    if (row.status !== "queued") continue;
    pending = true;
    const backingOff = row.send_after !== null && Date.parse(row.send_after) > nowMs;
    const fresh = nowMs - Date.parse(row.created_at) < NOTIFICATION_FAST_WINDOW_MS;
    if (!backingOff && fresh) return NOTIFICATION_FAST_POLL_MS;
  }
  return pending ? NOTIFICATION_SLOW_POLL_MS : false;
}

/** One page of the §2.20 log read. `limit` 50: a quote collects one row per
 * send, per reminder and one acknowledgement, so a second page is rare — but
 * it is offered rather than cut off, because a card that silently stops at a
 * page is a card that lies about what the customer was told. */
export const NOTIFICATION_PAGE_LIMIT = 50;

/**
 * Named, and typed by its own return, so TanStack pins the page type BEFORE
 * it reads the `refetchInterval` callback below (the messaging thread's
 * inference trap: an inline queryFn beside a data-reading callback collapses
 * the query to `unknown`).
 */
async function fetchCustomerNotificationsPage(
  query: CustomerNotificationsQuery,
  cursor: string | undefined,
  signal: AbortSignal,
): Promise<CustomerNotificationListResponse> {
  const res = await client.GET("/v1/customer-notifications", {
    signal,
    params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
  });
  return unwrap(res, "Could not load the customer's messages");
}

/**
 * §5 `GET /v1/customer-notifications?quote_id=` — the composer's Notifications
 * card (D-291 ruling 11: it rides `sales.read` with the document read's own
 * narrowing, so whoever can open the quote can read what its customer was
 * told). Newest first, the route's default order. Every loaded page is
 * re-read on the bounded poll, so a row on page two still flips.
 */
export function useQuoteNotifications(quoteId: string) {
  const query: CustomerNotificationsQuery = { quote_id: quoteId, limit: NOTIFICATION_PAGE_LIMIT };
  return useInfiniteQuery({
    queryKey: quoteKeys.notifications(query),
    enabled: quoteId.length > 0,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => fetchCustomerNotificationsPage(query, pageParam, signal),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    refetchInterval: (q) =>
      notificationRefetchInterval(
        q.state.data?.pages.flatMap((page) => page.items),
        Date.now(),
      ),
  });
}

/**
 * §3.3's off-cut suggestion + the D-241 ATP traffic-light, in ONE call.
 *
 * A MUTATION rather than a query, for the same reason `POST /v1/pricing/resolve`
 * is one: it is a POST-shaped dry run whose inputs are a form the manager is
 * still typing. Caching it under a query key would mean a cache entry per
 * (product, qty, uom, date) tuple — thousands of them in a session, every one
 * of them stale the moment stock moves, and none of them ever read twice.
 */
export function useFulfillmentOptions() {
  return useMutation({
    mutationFn: async (body: FulfillmentOptionsRequest) => {
      const res = await client.POST("/v1/sales/fulfillment-options", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not look up off-cut options");
    },
  });
}

// ---------------------------------------------------------------------------
// §3.4 quote writes
// ---------------------------------------------------------------------------

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuoteCreateRequest) => {
      const res = await client.POST("/v1/quotes", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the quote");
    },
    onSuccess: (_data, body) => {
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      // A quote created on a deal joins its linked quotes (E2-1).
      if (body.opportunity_id !== undefined) invalidateDealReadsFromDocument(qc);
    },
  });
}

export function useCreateRevision(quoteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuoteRevisionCreateRequest) => {
      const res = await client.POST("/v1/quotes/{id}/revisions", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not save the new version");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.quote(quoteId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.revisions(quoteId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
    },
  });
}

/**
 * §3.4's send. BOTH outcomes are successes: `sent` dispatched, and
 * `pending_approval` entered review. The screen branches on the discriminant
 * — never on "did it throw" — because a quote that went for approval is the
 * system working, not the send failing.
 *
 * It invalidates the REVIEW inbox too: a send that breached just created a
 * card on somebody else's screen, and the sender's own reload of /sales/reviews
 * must not show a stale feed.
 */
export function useSendQuote(quoteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuoteSendRequest) => {
      const res = await client.POST("/v1/quotes/{id}/send", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not send the quote");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.quote(quoteId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
      // The send writes its own §2.20 row (`quote_sent`, inline — D-291
      // ruling 2), and the composer's Notifications card is on the glass.
      void qc.invalidateQueries({ queryKey: quoteKeys.notificationLists() });
    },
  });
}

/** §3.4 decide (D-231's in-module review). Invalidates the inbox AND the
 * document: an approved quote's Send button appears the moment it lands. */
export function useDecideQuote(quoteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuoteDecideRequest) => {
      const res = await client.POST("/v1/quotes/{id}/decide", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not record the decision");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.quote(quoteId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
    },
  });
}

/** §3.4's explicit cancel — legal from draft / pending_approval / approved /
 * sent, terminal afterwards. The route revokes any live accept token, so the
 * customer's link stops offering a deal nobody will honour; the reason is
 * mandatory on the wire (`QuoteCancelRequest.reason` is `min(1)`), which is
 * why the §4.4 control asks for it before it will submit. */
export function useCancelQuote(quoteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string) => {
      const res = await client.POST("/v1/quotes/{id}/cancel", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { reason },
      });
      return unwrap(res, "Could not cancel the quote");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.quote(quoteId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      // A linked deal lists its quotes with their status, so a cancelled one
      // must not keep reading as sent on the deal page or in its peek.
      invalidateDealReadsFromDocument(qc);
    },
  });
}

/**
 * §5 record-rejection — "the customer said no on the phone".
 *
 * The `reason_code` is the point of the endpoint: §2.12's lost-reason
 * vocabulary is what makes P3-E's loss analysis possible at all, and a free
 * note typed instead of a code is a sentence no report can count. The note
 * rides BESIDE it, never in place of it, which is why the request carries both
 * and the screen offers both.
 */
export function useRecordRejection(quoteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuoteRecordRejectionRequest) => {
      const res = await client.POST("/v1/quotes/{id}/record-rejection", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not record the rejection");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.quote(quoteId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      // The deal's quote list and its Lost dialog's default reason read the
      // rejection, so the deal reads refresh with it.
      invalidateDealReadsFromDocument(qc);
    },
  });
}

/**
 * D-241's DUPLICATE. Not list-scoped like the others: it MINTS a document, and
 * the composer navigates straight to it carrying the §9.3 repriced/dropped
 * diff, so the caller wants the response rather than a refreshed list.
 */
export function useDuplicateQuote(quoteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuoteDuplicateRequest) => {
      const res = await client.POST("/v1/quotes/{id}/duplicate", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not duplicate the quote");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() }),
  });
}

// ---------------------------------------------------------------------------
// The §9.3 DUPLICATE DIFF, handed from the source document to the new one
// ---------------------------------------------------------------------------

/**
 * What the duplicate response says MOVED, carried to the screen that has to
 * say it.
 *
 * `repriced` and `dropped` are facts the server can state exactly once — in
 * the 201 body — and the document they are about is a DIFFERENT document from
 * the one the manager pressed the button on. Reading only `quote_id` and
 * navigating (what this screen did until 2026-09-10) throws both away: a
 * duplicate whose product left the price list comes back SHORTER, silently,
 * and §5's own contract note says "a silently shorter duplicate is the defect
 * this field exists to prevent".
 *
 * `labels` is here because the response names a moved line by `line_no` and
 * `product_id` and nothing else. A uuid is not something a manager can look
 * for in a yard, and the SOURCE document — which the composer is holding at
 * the moment it presses Duplicate — carries `description` per line. So the
 * label is resolved where it is known and travels with the diff.
 *
 * TWO SOURCES SINCE P3-D PR-D5 (D-289): a quote's Duplicate and an order's
 * Requote answer the same body, so `sourceNo` is whichever document the press
 * was made on — a quote number or an order number. For a requote, `labels` is
 * keyed by the ORDER's line numbers, because `dropped[].line_no` counts the
 * order's lines (the route's contract says so); `repriced[].line_no` counts the
 * new quote's and the banner never labels it.
 */
export interface QuoteDuplicateDiff {
  readonly sourceNo: string;
  readonly repriced: QuoteDuplicateResponse["repriced"];
  readonly dropped: QuoteDuplicateResponse["dropped"];
  /** `line_no` → the source document's own line description (§2.14's write-time
   * snapshot, not a live join). */
  readonly labels: Readonly<Record<number, string>>;
}

/**
 * ONE SLOT, not the query cache, and the reason is lifetime rather than taste.
 *
 * `setQueryData` on a key nothing observes creates an entry whose garbage
 * collector starts running the moment it is written (`gcTime`); the hand-over
 * would then be a race between a router navigation and a timer, which is the
 * class of bug that reproduces on a slow machine and nowhere else. Router
 * navigation state is the other candidate and costs a typed search param on a
 * route whose address a manager pastes to a colleague — a diff about a copy
 * they did not make.
 *
 * One slot is enough because one press produces one duplicate: a second press
 * before the first is claimed has superseded it anyway, and an unclaimed diff
 * is one object, not a leak.
 */
let pendingDuplicateDiff:
  { readonly quoteId: string; readonly diff: QuoteDuplicateDiff } | undefined;

export function handOverDuplicateDiff(quoteId: string, diff: QuoteDuplicateDiff): void {
  pendingDuplicateDiff = { quoteId, diff };
}

/** Take the diff waiting for THIS document, once. A second read answers
 * `undefined`, so a re-render cannot re-raise a banner the reader dismissed. */
export function claimDuplicateDiff(quoteId: string): QuoteDuplicateDiff | undefined {
  if (pendingDuplicateDiff?.quoteId !== quoteId) return undefined;
  const claimed = pendingDuplicateDiff.diff;
  pendingDuplicateDiff = undefined;
  return claimed;
}

/**
 * The arrival side: claim the diff for the document now on screen, exactly
 * once per document.
 *
 * The claim is keyed on `quoteId` in a REF rather than on mount, because the
 * composer is one component for both quote routes — navigating from the source
 * document to the duplicate changes the param without remounting anything, so
 * a mount-time claim would never fire. The ref is also what makes a StrictMode
 * double-invoked effect harmless: the second run sees the document already
 * claimed and leaves the banner alone instead of clearing it.
 */
export function useArrivingDuplicateDiff(quoteId: string): {
  diff: QuoteDuplicateDiff | undefined;
  dismiss: () => void;
} {
  const claimedFor = useRef<string | undefined>(undefined);
  const [diff, setDiff] = useState<QuoteDuplicateDiff | undefined>(undefined);
  useEffect(() => {
    if (claimedFor.current === quoteId) return;
    claimedFor.current = quoteId;
    setDiff(claimDuplicateDiff(quoteId));
  }, [quoteId]);
  return {
    diff,
    dismiss: () => {
      setDiff(undefined);
    },
  };
}

/**
 * The §4.5 BULK verbs, one quote per call.
 *
 * The bar fans out over the SELECTED, LOADED rows — never over "all N
 * matching", which cursor paging cannot know and which the primitive's own
 * contract forbids. Each hook takes the id per call so one bar press maps to
 * one mutation per row and a partial failure can be reported per row: §5
 * rate-limits reminders to one per quote per 72 h (`REMINDER_TOO_SOON`), so a
 * selection of ten will legitimately refuse some, and "3 sent, 2 refused" is
 * the honest answer where a single thrown error would lose the three.
 */
export function useRemindQuotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (quoteId: string) => {
      const res = await client.POST("/v1/quotes/{id}/remind", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not send the reminder");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      // A manual remind writes a §2.20 row inline (D-291 ruling 2).
      void qc.invalidateQueries({ queryKey: quoteKeys.notificationLists() });
    },
  });
}

export function useExpireQuotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (quoteId: string) => {
      const res = await client.POST("/v1/quotes/{id}/expire", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not expire the quote");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() }),
  });
}

// ---------------------------------------------------------------------------
// §4.6 review inbox
// ---------------------------------------------------------------------------

export function useReviews(query: ReviewsQuery, cursor: string | undefined, enabled = true) {
  return useQuery({
    queryKey: quoteKeys.reviews(query, cursor),
    enabled,
    // A page turn keeps the page shown until the next lands (D-560).
    placeholderData: keepPageOnTurn(quoteKeys.reviews(query, cursor)),
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/sales/reviews", {
        signal,
        params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not load the review inbox");
    },
  });
}

export function useCreditDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; body: CreditDecisionRequest }) => {
      const res = await client.POST("/v1/orders/{id}/credit-decision", {
        params: {
          path: { id: input.orderId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
        body: input.body,
      });
      return unwrap(res, "Could not record the credit decision");
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: quoteKeys.order(input.orderId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
    },
  });
}

// ---------------------------------------------------------------------------
// §2.9 customer price overrides — the three WRITES (P3-C-2, D-263)
//
// They sit here, beside the quote decide and the credit decision, because
// every one of them touches the §4.6 feed: a breaching proposal PUTS a
// `price_override` card there, a decision takes it out, and a revoke of a
// pending row withdraws it. Each invalidates the customer's override list
// (the §4.2 Pricing tab) and the inbox together, so neither surface can show
// a row the other has already moved on from.
// ---------------------------------------------------------------------------

/** §5 POST /v1/customer-price-overrides. The guard runs AT SUBMIT: the 201
 * answers `active` (inside the guard) or `pending_approval` with `review`
 * evidence — the caller reads the outcome off the row, never re-derives it. */
export function useCreateOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomerPriceOverrideCreateRequest) => {
      const res = await client.POST("/v1/customer-price-overrides", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not propose the price");
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: salesKeys.overrideLists(row.customer_id) });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
    },
  });
}

/** §5 POST /v1/customer-price-overrides/{id}/decide (D-231) — `approved` →
 * `active`, `rejected` → `revoked` with the mandatory note as the row's
 * reason. Emits `sales.override_review_decided` server-side. */
export function useDecideOverride(overrideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomerPriceOverrideDecideRequest) => {
      const res = await client.POST("/v1/customer-price-overrides/{id}/decide", {
        params: { path: { id: overrideId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not record the decision");
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: salesKeys.overrideLists(row.customer_id) });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
    },
  });
}

/** §5 POST /v1/customer-price-overrides/{id}/revoke — facility_admin /
 * group_admin only (service-narrowed under the shared code, D-263; a sales
 * user holding the code answers 403 naming the role, which is why the
 * Pricing tab gates the control on the ROLE). */
export function useRevokeOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: CustomerPriceOverrideRevokeRequest }) => {
      const res = await client.POST("/v1/customer-price-overrides/{id}/revoke", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not revoke the price");
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: salesKeys.overrideLists(row.customer_id) });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
    },
  });
}

// ---------------------------------------------------------------------------
// §4.7 orders (the READ surface this slice owns — the accept transaction
// creates them, and an order nobody can open is a document the product loses)
// ---------------------------------------------------------------------------

/** ONE PAGE of the §4.7 order list — `useOrders`' request as a plain function,
 * for the CSV export's walk (the `fetchQuotesPage` reasoning). */
export async function fetchOrdersPage(
  query: OrdersQuery,
  cursor: string | undefined,
  signal?: AbortSignal,
) {
  const res = await client.GET("/v1/orders", {
    ...(signal !== undefined ? { signal } : {}),
    params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
  });
  return unwrap(res, "Could not load the orders");
}

export function useOrders(query: OrdersQuery, cursor: string | undefined, enabled = true) {
  return useQuery({
    queryKey: quoteKeys.orders(query, cursor),
    enabled,
    // A page turn keeps the page shown until the next lands (D-560).
    placeholderData: keepPageOnTurn(quoteKeys.orders(query, cursor)),
    queryFn: async ({ signal }) => fetchOrdersPage(query, cursor, signal),
  });
}

/** `useQuotePages` for the order list (§4.14's Mark won picker, #293): every
 * walked page stays live, so `quoteKeys.orderLists()` reads them all again. */
export function useOrderPages(query: OrdersQuery, cursors: readonly (string | undefined)[]) {
  return useQueries({
    queries: cursors.map((cursor) => ({
      queryKey: quoteKeys.orders(query, cursor),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchOrdersPage(query, cursor, signal),
    })),
  });
}

/** Named and typed by its own return, for the `refetchInterval` inference
 * reason `fetchCustomerNotificationsPage` states. */
async function fetchOrder(id: string, signal: AbortSignal): Promise<OrderDetail> {
  const res = await client.GET("/v1/orders/{id}", { signal, params: { path: { id } } });
  return unwrap(res, "Could not load the order");
}

/**
 * §4.7's order detail. It re-reads on the §2.20 BOUNDED POLL
 * (`notificationRefetchInterval`) while any of its notifications is `queued`
 * or `held` (P3-E PR-E1, D-291): a confirm, a release, an override or a
 * cancel writes the customer's message `queued` in its own transaction and the
 * dispatcher sends it seconds later, so without the poll the panel would show
 * every push as still going out. The whole detail is re-read rather than a
 * second list beside it, so the panel and the timeline it explains can never
 * disagree; once nothing is pending the query is quiet again.
 */
/**
 * THE PROMISE POLL (P3-J J-2, D-465 ruling 3). While the order's Confirmed
 * message waits for its dates (`confirmed_notice_due_at` set), or while the
 * reader has just committed a promise onto a line whose stamp has not landed
 * (`awaitingStamp`), module 03 is about to date a line and module 09 to stamp
 * it, so the detail re-reads: fast for the first two minutes after the order
 * confirmed (the drain normally settles in seconds), slow after that until the
 * deadline clears the stamp. Combined with the notification poll, the shorter
 * cadence wins.
 */
export function orderRefetchInterval(
  order:
    Pick<OrderDetail, "notifications" | "confirmed_notice_due_at" | "confirmed_at"> | undefined,
  nowMs: number,
  awaitingStamp = false,
): number | false {
  if (order === undefined) return false;
  const notices = notificationRefetchInterval(order.notifications, nowMs);
  let promise: number | false = false;
  if (awaitingStamp) {
    promise = NOTIFICATION_FAST_POLL_MS;
  } else if ((order.confirmed_notice_due_at ?? null) !== null) {
    const confirmed = order.confirmed_at === null ? Number.NaN : Date.parse(order.confirmed_at);
    const fresh = Number.isFinite(confirmed) && nowMs - confirmed < NOTIFICATION_FAST_WINDOW_MS;
    promise = fresh ? NOTIFICATION_FAST_POLL_MS : NOTIFICATION_SLOW_POLL_MS;
  }
  if (notices === false) return promise;
  if (promise === false) return notices;
  return Math.min(notices, promise);
}

export function useOrder(id: string, options: { readonly awaitingStamp?: boolean } = {}) {
  const awaitingStamp = options.awaitingStamp ?? false;
  return useQuery<OrderDetail>({
    queryKey: quoteKeys.order(id),
    enabled: id.length > 0,
    queryFn: ({ signal }) => fetchOrder(id, signal),
    refetchInterval: (query) => orderRefetchInterval(query.state.data, Date.now(), awaitingStamp),
  });
}

export function useCancelOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string) => {
      const res = await client.POST("/v1/orders/{id}/cancel", {
        params: { path: { id: orderId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { reason },
      });
      return unwrap(res, "Could not cancel the order");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.order(orderId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      // A deal this order won stays won with its order cancelled (E2-1).
      invalidateDealReadsFromDocument(qc);
    },
  });
}

/**
 * Re-promise one line on a fresh quoted promise (spec 09 §4.7 and §5, spec 03
 * §5 row 79, D-469). Answers the order with the line's new date, which is
 * written straight into the detail's cache, and re-reads the order lists so
 * the at-risk flag follows. A refusal is thrown as the route's
 * `RequestError` (BASIS_STALE carries `fresh_promise`), which the CTP dialog
 * reads.
 */
export function useRePromiseLine(orderId: string) {
  const qc = useQueryClient();
  return useCallback(
    async (lineId: string, promiseId: string, notifyCustomer: boolean): Promise<OrderDetail> => {
      const res = await client.POST("/v1/orders/{id}/re-promise", {
        params: { path: { id: orderId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: {
          sales_order_line_id: lineId,
          promise_id: promiseId,
          notify_customer: notifyCustomer,
        },
      });
      const order = unwrap(res, "Could not re-promise the line");
      qc.setQueryData(quoteKeys.order(orderId), order);
      void qc.invalidateQueries({ queryKey: quoteKeys.order(orderId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      return order;
    },
    [orderId, qc],
  );
}

// ---------------------------------------------------------------------------
// D-241: §2.2 customer assignments (owner + collaborators)
// ---------------------------------------------------------------------------

/**
 * Both writes invalidate the CUSTOMER 360, which is where the assignment set
 * is served from (`Customer360Response.assignments`) — there is no separate
 * GET, and adding one would be a second read of data the 360 already carries.
 *
 * They also invalidate the QUOTE LISTS: `owner_user_id` is a §4.5 filter, and
 * re-pointing ownership silently re-buckets every quote for that customer.
 */
export function useAddAssignment(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomerAssignmentCreateRequest) => {
      const res = await client.POST("/v1/customers/{id}/assignments", {
        params: { path: { id: customerId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not add the assignment");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.customer(customerId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
    },
  });
}

export function useRemoveAssignment(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await client.DELETE("/v1/customers/{id}/assignments/{userId}", {
        params: {
          path: { id: customerId, userId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
      });
      unwrapVoid(res, "Could not remove the assignment");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.customer(customerId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
    },
  });
}
